import { DISTANCE_UNITS } from '@config/constants';
import { InternalLink } from '../../Clickables/InternalLink/InternalLink';
import { Heading } from '../../Heading/Heading';
import { LocationLinks } from '../../LocationLinks/LocationLinks';
import { firstNonEmpty } from '@lib/strings/firstNonEmpty';
import { Text } from '../../Text/Text';

interface IProps {
  distance?: number;
  country: {
    id: string;
    countryName: string;
  };
  region: {
    id: string | null;
    name: string | null;
    localName?: string | null;
  };
  placeName: string;
  placeUrl: string;
  distanceUnit?: (typeof DISTANCE_UNITS)[keyof typeof DISTANCE_UNITS];
  isHidden?: boolean;
  showLocationLinks?: boolean;
}

/**
 * Component to display an entry for a nearby place with its distance.
 *
 * @param {number} distance - The distance to the place.
 * @param {string} placeName - The name of the place.
 * @param {string} placeUrl - The URL to the place.
 * @param {keyof typeof DISTANCE_UNITS} distanceUnit - The unit of distance (e.g., miles, kilometers).
 * @param {boolean} [isHidden=false] - Whether the entry should be hidden.
 *
 * @returns {JSX.Element} The rendered component.
 */
export const PlaceEntry = ({
  distance,
  region,
  country,
  placeName,
  placeUrl,
  distanceUnit,
  isHidden = false,
  showLocationLinks = true,
}: IProps) => {
  return (
    <div className={`flex flex-col ${isHidden ? 'hidden' : ''}`}>
      <InternalLink to={placeUrl}>
        <Heading as="span" variant="2xs">
          {placeName}
        </Heading>
      </InternalLink>
      {distance != null && (
        <Text variant="xs">{`${distance.toFixed(1)} ${distanceUnit} away`}</Text>
      )}
      {showLocationLinks && (
        <LocationLinks
          regionId={region.id}
          regionName={region.name}
          regionDisplayName={firstNonEmpty(region.localName, region.name)}
          countryId={country.id}
          countryName={country.countryName}
          textVariant="xs"
          LinkClassName="text-abyss! font-normal!"
          className="text-left font-normal"
        />
      )}
    </div>
  );
};
